import { useEffect, useRef, useState, type FormEvent } from 'react'
import { SendHorizontal, X } from 'lucide-react'

type Props = {
  open: boolean
  pending: boolean
  error: string | null
  initialPrompt?: string
  onCancel: () => void
  onSubmit: (openingPrompt: string) => void | Promise<void>
}

export default function NewTopicPromptDialog({
  open,
  pending,
  error,
  initialPrompt = '',
  onCancel,
  onSubmit,
}: Props) {
  const [draft, setDraft] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    if (!open) {
      setDraft('')
      return
    }
    setDraft(initialPrompt)
    const id = window.setTimeout(() => textareaRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [initialPrompt, open])

  if (!open) return null

  const trimmed = draft.trim()

  async function submit(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault()
    if (!trimmed || pending) return
    await onSubmit(trimmed)
  }

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-stone-950/50 p-3 sm:items-center"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !pending) onCancel()
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-topic-title"
        aria-busy={pending}
        onSubmit={submit}
        onMouseDown={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-xl border border-stone-200 bg-white p-3 shadow-2xl dark:border-stone-700 dark:bg-stone-900"
      >
        <div className="flex items-center gap-2">
          <h2 id="new-topic-title" className="min-w-0 flex-1 text-sm font-semibold text-stone-900 dark:text-stone-100">
            New topic
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="grid size-8 place-items-center rounded-lg text-stone-500 transition-colors hover:bg-stone-100 disabled:opacity-50 dark:hover:bg-stone-800"
            aria-label="Close"
          >
            <X className="size-4" />
          </button>
        </div>
        <label htmlFor="new-topic-opening-prompt" className="mt-2 block text-xs font-medium text-stone-600 dark:text-stone-300">
          Opening prompt
        </label>
        <textarea
          ref={textareaRef}
          id="new-topic-opening-prompt"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !pending) onCancel()
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void submit()
          }}
          disabled={pending}
          rows={7}
          placeholder="Ask the agent to start here"
          className="mt-1 min-h-36 w-full resize-y rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm leading-5 text-stone-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60 dark:border-stone-700 dark:bg-stone-950 dark:text-stone-100"
        />
        {error && (
          <p role="alert" className="mt-2 rounded-lg bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-200">
            {error}
          </p>
        )}
        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="rounded-lg px-3 py-2 text-sm text-stone-600 transition-colors hover:bg-stone-100 disabled:opacity-50 dark:text-stone-300 dark:hover:bg-stone-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!trimmed || pending}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-stone-300 dark:disabled:bg-stone-700"
          >
            <SendHorizontal className="size-4" />
            {pending ? 'Naming' : 'Start'}
          </button>
        </div>
      </form>
    </div>
  )
}
